import { useAtom, useCtx } from '@reatom/npm-react'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { AsyncStateN } from '@blade/std-lib'
import { Numeric } from '@blade/ui-kit'
import { Trend } from '@root/entities/trend'
import {
  activePairPositionModel,
  activePairPositionStateAtom,
} from '@root/features/trade/trading'
import { PnlUsdFormatted } from '@root/shared/core/format'

import { ActivePositionDetail } from './active-position-detail'

import type { WalletPositionModel } from '@root/entities/position'

type Props = {}

export const ActivePositionDetails = React.memo<Props>(() => {
  const { t } = useTranslation()

  const [model] = useAtom(activePairPositionModel)
  const [positionState] = useAtom(activePairPositionStateAtom)
  const ctx = useCtx()

  const placeholder = useMemo(() => {
    if (AsyncStateN.isSuccess(positionState)) {
      return AsyncStateN.success({
        el: '-',
        trend: undefined,
      })
    }

    return positionState
  }, [positionState])

  const handleRetry = useCallback(() => {
    model?.execute(ctx)
  }, [model, ctx])

  if (AsyncStateN.isSuccess(positionState) && positionState.value) {
    return <Ready position={positionState.value} />
  }

  return (
    <Root>
      <ActivePositionDetail
        title={t('trading.position.invested')}
        value={placeholder}
        valueSecondary={placeholder}
        onRetry={handleRetry}
      />
      <ActivePositionDetail
        title={t('trading.position.sold')}
        value={placeholder}
        valueSecondary={placeholder}
        onRetry={handleRetry}
      />
      <ActivePositionDetail
        title={t('trading.position.remaining')}
        value={placeholder}
        valueSecondary={placeholder}
        onRetry={handleRetry}
      />
      <ActivePositionDetail
        title={t('common.upnl')}
        tooltip={t('common.upnl-tooltip')}
        value={placeholder}
        valueSecondary={placeholder}
        onRetry={handleRetry}
      />
    </Root>
  )
})

const Root = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;

  border-radius: 12px;
  border: 1px solid var(--background-2);
  padding: 1px;
  gap: 2px;
`

const Ready = ({ position }: { position: WalletPositionModel }) => {
  const { t } = useTranslation()
  const [pnl] = useAtom(position.unrealizedPnlUsd)
  const [boughtSizeUsd] = useAtom(position.boughtSizeUsd)
  const [boughtSizeCurrency] = useAtom(position.boughtSizeCurrency)
  const [soldSizeUsd] = useAtom(position.soldSizeUsd)
  const [soldSizeCurrency] = useAtom(position.soldSizeCurrency)
  const [remainingSizeUsd] = useAtom(position.remainingSizeUsd)
  const [remainingSizeCurrency] = useAtom(position.remainingSizeCurrency)

  const boughtSizeUsdState = useMemo(() => {
    return AsyncStateN.success({
      el: (
        <Numeric
          preset="currency-amount-short"
          value={boughtSizeUsd}
          currency="$"
        />
      ),
      trend: Trend.Up,
    })
  }, [boughtSizeUsd])

  const boughtSizeCurrencyState = useMemo(() => {
    return AsyncStateN.success({
      el: <Numeric preset="currency-amount-short" value={boughtSizeCurrency} />,
      trend: Trend.Neutral,
    })
  }, [boughtSizeCurrency])

  const soldSizeUsdState = useMemo(() => {
    return AsyncStateN.success({
      el: (
        <Numeric
          preset="currency-amount-short"
          value={soldSizeUsd}
          currency="$"
        />
      ),
      trend: Trend.Down,
    })
  }, [soldSizeUsd])

  const soldSizeCurrencyState = useMemo(() => {
    return AsyncStateN.success({
      el: <Numeric preset="currency-amount-short" value={soldSizeCurrency} />,
      trend: Trend.Neutral,
    })
  }, [soldSizeCurrency])

  const remainingSizeUsdState = useMemo(() => {
    return AsyncStateN.success({
      el: (
        <Numeric
          preset="currency-amount-short"
          value={remainingSizeUsd}
          currency="$"
        />
      ),
      trend: Trend.Up,
    })
  }, [remainingSizeUsd])

  const remainingSizeCurrencyState = useMemo(() => {
    return AsyncStateN.success({
      el: (
        <Numeric preset="currency-amount-short" value={remainingSizeCurrency} />
      ),
      trend: Trend.Neutral,
    })
  }, [remainingSizeCurrency])

  const pnlChangeUsd = useMemo(() => {
    if (remainingSizeCurrency.isZero()) {
      return AsyncStateN.success({
        el: <span>{t('trade.pnl.sold-all')}</span>,
        trend: Trend.Down,
      })
    }
    return AsyncStateN.success({
      el: <PnlUsdFormatted pnl={pnl.pnlUsd} />,
      trend: Trend.of(pnl.pnlUsd),
    })
  }, [pnl.pnlUsd, remainingSizeCurrency, t])

  const pnlChangePercent = useMemo(() => {
    if (remainingSizeCurrency.isZero()) {
      return AsyncStateN.success({
        el: <span>&nbsp;</span>,
        trend: Trend.Neutral,
      })
    }
    return AsyncStateN.success({
      el: (
        <span>
          <Numeric preset="percents" value={pnl.pnlPercentage} forceSign />%
        </span>
      ),
      trend: Trend.of(pnl.pnlPercentage),
    })
  }, [pnl.pnlPercentage, remainingSizeCurrency])

  return (
    <Root>
      <ActivePositionDetail
        title={t('trading.position.invested')}
        value={boughtSizeUsdState}
        valueSecondary={boughtSizeCurrencyState}
        onRetry={() => {}}
      />
      <ActivePositionDetail
        title={t('trading.position.sold')}
        value={soldSizeUsdState}
        valueSecondary={soldSizeCurrencyState}
        onRetry={() => {}}
      />
      <ActivePositionDetail
        title={t('trading.position.remaining')}
        value={remainingSizeUsdState}
        valueSecondary={remainingSizeCurrencyState}
        onRetry={() => {}}
      />
      <ActivePositionDetail
        title={t('common.upnl')}
        value={pnlChangeUsd}
        valueSecondary={pnlChangePercent}
        onRetry={() => {}}
      />
    </Root>
  )
}
